<script setup lang="ts">
import { useRoute } from 'vue-router'
const isEn = useRoute().meta.locale === 'en'
</script>

<template>
  <section class="contact-page page-first" aria-labelledby="contact-title">
    <div class="contact-heading">
      <p class="section-index">01 / {{ isEn ? 'CONTACT' : 'KONTAKT' }}</p>
      <h1 id="contact-title">{{ isEn ? 'Let’s talk about labs for your organisation.' : 'Porozmawiajmy o laboratoriach dla Twojej organizacji.' }}</h1>
      <p>{{ isEn ? 'Every message is read by someone close to the project. Tell us how you run your courses and we will come back with questions about the real context, with no automated sales reply.' : 'Każdą wiadomość czyta człowiek związany z projektem. Opisz sposób prowadzenia zajęć, a wrócimy z pytaniami o realny kontekst, bez automatycznej odpowiedzi sprzedażowej.' }}</p>
    </div>

    <div class="contact-grid">
      <div class="contact-form-wrap">
        <p class="eyebrow">{{ isEn ? 'SEND A MESSAGE' : 'WYŚLIJ WIADOMOŚĆ' }}</p>
        <iframe
          :src="isEn ? 'https://interfaces.zapier.com/embed/page/cmje5wows002714869yrqe9p1?noBackground=false' : 'https://interfaces.zapier.com/embed/page/cmjea49ze0061zbwre8ag7wk2?noBackground=false'"
          :title="isEn ? 'PentestCamp contact form' : 'Formularz kontaktowy PentestCamp'"
          loading="lazy"
          referrerpolicy="strict-origin-when-cross-origin"
        />
        <p class="form-note">{{ isEn ? 'The form is provided by Zapier. You can also email us directly at' : 'Formularz jest obsługiwany przez Zapier. Możesz też napisać bezpośrednio na' }} <a href="mailto:contact@dsecure.me?subject=PentestCamp">contact@dsecure.me</a>.</p>
      </div>

      <aside class="contact-aside" aria-labelledby="contact-checklist-title">
        <p class="eyebrow">{{ isEn ? 'A GOOD STARTING POINT' : 'DOBRY PUNKT STARTU' }}</p>
        <h2 id="contact-checklist-title">{{ isEn ? 'What should you include?' : 'Co warto zawrzeć w wiadomości?' }}</h2>
        <ul>
          <li><span>01</span>{{ isEn ? 'The name of your university, company or training team.' : 'Nazwa uczelni, firmy lub zespołu szkoleniowego.' }}</li>
          <li><span>02</span>{{ isEn ? 'Approximate number of groups and participants.' : 'Przybliżona liczba grup i uczestników.' }}</li>
          <li><span>03</span>{{ isEn ? 'Preferred model: cloud or on-premise.' : 'Preferowany model: cloud czy on-premise.' }}</li>
          <li><span>04</span>{{ isEn ? 'Lab topics and course cadence.' : 'Tematy laboratoriów i rytm zajęć.' }}</li>
          <li><span>05</span>{{ isEn ? 'Your intended launch date.' : 'Planowany termin uruchomienia.' }}</li>
        </ul>
        <section class="contact-details" :aria-label="isEn ? 'DSecure.me contact information' : 'Dane kontaktowe DSecure.me'">
          <h3>{{ isEn ? 'Contact information' : 'Dane kontaktowe' }}</h3>
          <dl>
            <div>
              <dt>{{ isEn ? 'Email' : 'E-mail' }}</dt>
              <dd><a href="mailto:contact@dsecure.me?subject=PentestCamp">contact@dsecure.me</a></dd>
            </div>
            <div>
              <dt>{{ isEn ? 'Registered office' : 'Siedziba' }}</dt>
              <dd><address>ul. Na Grobli 12, bud. L3, pok. 021<br>50-421 Wrocław<span v-if="isEn">, Poland</span></address></dd>
            </div>
            <div>
              <dt>{{ isEn ? 'Company' : 'Firma' }}</dt>
              <dd>DSecure.me Sp. z o.o.<br>KRS 0000887405<br>NIP 8971889380</dd>
            </div>
          </dl>
          <div class="contact-socials">
            <a href="https://www.linkedin.com/company/dsecure-me/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            <a href="https://github.com/orgs/DSecureMe/" target="_blank" rel="noopener noreferrer">GitHub</a>
          </div>
        </section>
      </aside>
    </div>

    <section class="contact-trust" :aria-labelledby="isEn ? 'contact-trust-title-en' : 'contact-trust-title-pl'">
      <p class="eyebrow">{{ isEn ? 'WHO ANSWERS' : 'KTO ODPOWIADA' }}</p>
      <h2 :id="isEn ? 'contact-trust-title-en' : 'contact-trust-title-pl'">{{ isEn ? 'Your message goes to the team responsible for PentestCamp.' : 'Wiadomość trafia do zespołu odpowiedzialnego za PentestCamp.' }}</h2>
      <p>{{ isEn ? 'PentestCamp is a product of DSecure.me Sp. z o.o., registered in Poland under KRS 0000887405 and NIP 8971889380. We use the information you provide to understand your course or training context, answer your questions and discuss a possible cloud or on-premise deployment. We do not publish contact-form submissions or use an automated sales response.' : 'PentestCamp jest produktem DSecure.me Sp. z o.o., zarejestrowanej w Polsce pod numerami KRS 0000887405 i NIP 8971889380. Podane informacje wykorzystujemy, aby zrozumieć kontekst zajęć lub szkolenia, odpowiedzieć na pytania i omówić możliwe wdrożenie cloud lub on-premise. Nie publikujemy zgłoszeń z formularza i nie korzystamy z automatycznej odpowiedzi sprzedażowej.' }}</p>
      <p>{{ isEn ? 'The embedded form is provided by Zapier. If you prefer not to use it, send an email directly to contact@dsecure.me. Details about the organisation and the handling of website data are available on the About and Privacy pages.' : 'Osadzony formularz jest dostarczany przez Zapier. Jeśli nie chcesz z niego korzystać, wyślij wiadomość bezpośrednio na contact@dsecure.me. Dane organizacji oraz zasady przetwarzania informacji na stronie opisujemy na stronach O nas i Prywatność.' }}</p>
      <div class="trust-links">
        <a :href="isEn ? '/en/about/' : '/about/'">{{ isEn ? 'About PentestCamp' : 'O PentestCamp' }}</a>
        <a :href="isEn ? '/en/privacy/' : '/privacy/'">{{ isEn ? 'Privacy information' : 'Informacje o prywatności' }}</a>
      </div>
    </section>
  </section>
</template>
